import React from 'react';
import styled from 'styled-components';
import { useParams } from '@reach/router';

import { PageStyled } from '@modules/layout/styled';
import { useBaseLayout } from '@modules/layout/templates';
import { Loader } from '@modules/layout/moleculas';
import {
    useOnlineStudentPerformance,
    useStudent,
    useStudentPerformanceStatsFiltersQuery,
} from '@modules/students/hooks';
import { StudentPerformanceTable, StudentPerformanceStats } from '@modules/students/organisms';
import { StudentPerformanceStatsRequest } from '@modules/students/requests';

const Root = styled(PageStyled.Root)`
    height: 100%;
    padding-bottom: 60px;
`;

const StudentPerformanceTab = (): React.ReactElement => {
    const params = useParams();
    const studentId: string | undefined = params.id;

    const { setPageTitle } = useBaseLayout();
    const { filters } = useStudentPerformanceStatsFiltersQuery();
    const studentPerformanceStatsRequest = new StudentPerformanceStatsRequest(filters);

    const { student, loading: studentLoading } = useStudent({
        skip: !studentId,
        variables: { id: studentId as string },
    });

    const { onlineStudentPerformance, loading: onlineStudentPerformanceLoading } =
        useOnlineStudentPerformance({
            fetchPolicy: 'network-only',
            skip: !filters.courseId || !student.attachedUser.id,
            variables: {
                courseId: filters.courseId as string,
                userId: student.attachedUser.id,
                orderBy: studentPerformanceStatsRequest.orderBy,
            },
        });

    React.useEffect(() => {
        setPageTitle(student.fullName);
    }, [student.fullName]);

    if (studentLoading || onlineStudentPerformanceLoading) {
        return <Loader />;
    }

    return (
        <Root>
            <StudentPerformanceStats coursePerformance={onlineStudentPerformance.course} />

            <StudentPerformanceTable
                courseId={filters.courseId as string}
                userId={student.attachedUser.id}
                performances={onlineStudentPerformance.subjects}
            />
        </Root>
    );
};

export { StudentPerformanceTab };
